import type { BatchRegisterRow } from '@api/vendor-portal';
import QuantityInput from '@components/ui/QuantityInput';
import StatusPill from '@components/ui/StatusPill';
import { hasCasePack } from '@diagon-alley/common';
import useToaster from '@hooks/toaster/useToaster';
import {
    Button,
    Group,
    Modal,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { toOrderLines } from './batchGridRows';
import { type OnBehalfOf, portalApi, portalScope } from './portalContext';
import styles from './ShipmentBuilderModal.module.css';
import {
    batchLabel,
    buildShipmentRows,
    checkShipment,
    fefoNudges,
    formatDate,
    orderOptionsForRow,
    outstandingByWorkOrder,
    splitRow,
    summarise,
    toDispatchLines,
    type ShipmentRow,
} from './shipmentRows';
import { formatCount } from '@utils/currency';

/** Hints sit under the box, so boxes in a row line up whatever the hint. */
const BELOW: Array<'label' | 'input' | 'description' | 'error'> = [
    'label',
    'input',
    'description',
    'error',
];

/**
 * "Available: 150" — what the batch has left, under the box that draws on it. The case reading
 * is the quantity box's own business: `QuantityInput` prints it for whatever is typed.
 */
const describeShelf = (available: number): string => `Available: ${formatCount(available)}`;

interface ShipmentBuilderModalProps {
    /** The batches picked off the register. Empty means closed. */
    batches: BatchRegisterRow[];
    onClose: () => void;
    onShipped: () => void;
    /**
     * Which door is sending, and therefore whose yard the goods are leaving.
     *
     * Null is the manufacturer shipping from its own portal — the server reads the site off the
     * login, so nothing here names it. Set is the brand acting for a named site.
     *
     * No reporter travels with it either way. Each door records who it was without being told:
     * the brand's names the brand user, the site's names the site login.
     */
    onBehalfOf?: OnBehalfOf | null;
    /**
     * Whether the quantity boxes open blank.
     *
     * Follows the Dispatch list's own grain: somebody who picked products is about to be told
     * "send 400 of this" and types it per batch, so a box pre-filled with a whole batch would
     * make the load look decided before anyone typed. Somebody who picked batch codes chose the
     * batch, so the box opens on all of it. The lines posted are identical either way.
     */
    groupByProduct?: boolean;
}

/**
 * One consignment, built from the batches somebody just picked.
 *
 * THE GRAIN IS THE LORRY, not the batch and not the order. A load going back to the brand
 * routinely holds several runs across several SKUs answering more than one order; recording each
 * as its own shipment would turn one departure into three deliveries in the brand's planning and
 * three documents at the gate.
 *
 * REACHED FROM THE BATCH REGISTER, which is where the runs are. That is not a layout
 * convenience: whoever is deciding what goes on the lorry is reading the register to do it, and
 * putting the act of building the load anywhere else would mean leaving the screen that just
 * told them what came off the line and what state it is in.
 *
 * A VERDICT IS NOT A PREREQUISITE. Unruled batches may travel — the brand inspects the load at
 * its own dock — so this offers them alongside released ones and refuses only what has been
 * ruled against.
 *
 * THE ORDER IS PER ROW. A batch's own work-order link is provenance and is empty rather often,
 * so it fills the picker in and does not decide it. Leaving a row's order blank is a real answer:
 * surplus going back as stock answers nothing.
 *
 * ONE LINE PER BATCH, AND NOTHING ABOVE IT. There used to be a quantity box per product that
 * split what was typed across the batches oldest-first, with the batch lines underneath as
 * what the split did. Two numbers that had to agree meant a reconciliation message, a pool of
 * batches the split had not drawn on, and a way to put them back - and the split walked the
 * batches in register order, newest first, so it was not oldest-first at all. Now every box
 * means one batch, one quantity, one order.
 *
 * EVERY ROW THE SAME SHAPE. A product's name is said once, bold, on its first line, and its
 * further batches leave that cell blank the way a spreadsheet does - the first cut gave those
 * products a shaded label row and set their lines in from the margin, and a single-batch
 * product beside them then read as one more batch of the product above. The batch column
 * carries what is the batch's: its code, when it goes off, and on a product's first line the
 * mark that it is the one to send first. Lines are in dispatch order; a line that draws on a
 * later batch while a sooner one still has stock is told so under the box, and not stopped.
 */
const ShipmentBuilderModal: FC<ShipmentBuilderModalProps> = ({
    batches,
    onClose,
    onShipped,
    onBehalfOf,
    groupByProduct = false,
}) => {
    const { showToastSuccess, showToastError } = useToaster();
    const api = useMemo(() => portalApi(onBehalfOf), [onBehalfOf]);
    const scope = portalScope(onBehalfOf);

    const opened = batches.length > 0;

    const [rows, setRows] = useState<ShipmentRow[]>([]);
    /** Only ever incremented, so two splits of one batch cannot collide on a line id. */
    const [splitSeq, setSplitSeq] = useState(1);
    const [dispatchDate, setDispatchDate] = useState('');
    const [reference, setReference] = useState('');
    const [transportReference, setTransportReference] = useState('');
    const [notes, setNotes] = useState('');
    /**
     * Which of the brand's warehouses the load is going to.
     *
     * NOT the site the goods are leaving, which no longer appears here at all — each door knows
     * its own. The name is `destinationId` rather than warehouseId because the API's request
     * field is also called warehouseId, and the two must not be swapped silently.
     */
    const [destinationId, setDestinationId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data: orderData } = useQuery(
        [scope, 'vendor-orders'],
        api.getOrders,
        { enabled: opened, staleTime: 60 * 1000 },
    );

    // Only for the case size per product; the pickable products themselves come from the batches.
    const { data: catalog } = useQuery([scope, 'vendor-catalog'], api.getCatalog, {
        staleTime: 5 * 60 * 1000,
    });
    const unitsPerCaseOf = useCallback(
        (itemId: string) =>
            catalog?.products.find((product) => product.itemId === itemId)?.unitsPerCase ?? null,
        [catalog],
    );
    const { data: destinations } = useQuery(
        [scope, 'dispatch-destinations'],
        api.getDestinations,
        // The brand's warehouses barely change; once per session is plenty.
        { enabled: opened, staleTime: 5 * 60 * 1000 },
    );

    /**
     * The name this load will get, written INTO the field so it reads as the name and not as
     * a hint. Whoever wants another name deletes it and types. The number is offered, not
     * reserved: if two loads are built at once the second save is refused as a duplicate and
     * the person is told, rather than two lorries silently sharing "S4".
     */
    const { data: nextReference } = useQuery(
        [scope, 'next-shipment-reference', batches.length],
        api.getNextReference,
        { enabled: opened, staleTime: 0 },
    );

    useEffect(() => {
        if (!nextReference?.reference) return;
        // Only into an empty field: a name somebody typed is never overwritten by a refetch.
        setReference((current) => current || nextReference.reference);
    }, [nextReference?.reference, batches]);

    /**
     * What the lanes say about this site's doors, read off the rows rather than asked for.
     *
     * `known` is false against a server that has not started sending `laneBacked`, and then
     * the picker is the flat list it always was. `hasLanes` is the clause that matters: with
     * any lane recorded the dispatch gate refuses every other door, so those are shown but not
     * offered; with none recorded every door is open and the hint says why the list is wide.
     */
    const laneState = useMemo(() => {
        const doors = destinations ?? [];
        return {
            known: doors.some((door) => door.laneBacked !== undefined),
            hasLanes: doors.some((door) => door.laneBacked === true),
            vendorName: doors.find((door) => door.vendorName)?.vendorName ?? null,
        };
    }, [destinations]);

    const destinationOptions = useMemo(() => {
        const doors = destinations ?? [];
        const toItem = (door: (typeof doors)[number], disabled = false) => ({
            value: door.warehouseId,
            label: door.name,
            disabled,
        });

        if (!laneState.hasLanes) return doors.map((door) => toItem(door));

        // Lane-backed first - the server already sorts them so, the group makes it visible -
        // and the rest listed under why they cannot be picked, rather than quietly missing.
        return [
            {
                group: 'Lanes from this site',
                items: doors.filter((door) => door.laneBacked).map((door) => toItem(door)),
            },
            {
                group: `No lane from ${laneState.vendorName ?? 'this site'} yet`,
                items: doors.filter((door) => !door.laneBacked).map((door) => toItem(door, true)),
            },
        ].filter((group) => group.items.length > 0);
    }, [destinations, laneState]);

    /**
     * The muted line under the picker when the brand has recorded no lanes for this site.
     * The site cannot add one itself, so its door is told who can.
     */
    const noLanesHint =
        laneState.known && !laneState.hasLanes && (destinations?.length ?? 0) > 0
            ? `No lanes from ${laneState.vendorName ?? 'this site'} yet — ${
                  onBehalfOf ? 'add one on the Lanes page' : 'ask the brand to add one'
              }`
            : undefined;

    const orders = useMemo(() => orderData?.orders ?? [], [orderData]);
    const orderLines = useMemo(() => toOrderLines(orders), [orders]);
    const outstanding = useMemo(() => outstandingByWorkOrder(orders), [orders]);

    /**
     * Every open order asking for a SKU, as picker options - what a FREE batch may fill.
     * A batch with real provenance never sees these; its own order and stock are the offer.
     */
    const orderChoicesBySku = useMemo(() => {
        const bySku = new Map<string, Array<{ value: string; label: string }>>();
        const seen = new Map<string, Set<string>>();

        for (const line of orderLines) {
            if (!line.productId) continue;
            const choice = line.productionOrderId
                ? { value: `po:${line.productionOrderId}`, label: line.productionOrderCode ?? line.code }
                : { value: `wo:${line.workOrderId}`, label: line.code };

            const seenForSku = seen.get(line.productId) ?? new Set<string>();
            if (seenForSku.has(choice.value)) continue;
            seenForSku.add(choice.value);
            seen.set(line.productId, seenForSku);
            bySku.set(line.productId, [...(bySku.get(line.productId) ?? []), choice]);
        }

        return bySku;
    }, [orderLines]);

    /**
     * Re-seeded whenever the picked set changes, and only then.
     *
     * The orders arrive after the modal opens, so the default order per row is filled in once
     * they land — a person who has already answered a row would otherwise have their answer
     * overwritten by a late-arriving default.
     */
    useEffect(() => {
        if (!opened) return;
        // BLANK, NOT "EVERYTHING", in product mode. The number somebody was told to send is
        // the input; opening on the whole batch made a full load the default and a smaller one
        // the edit, which is backwards. In batch mode the batch was the choice, so it keeps
        // opening on all of it.
        setRows(
            groupByProduct
                ? buildShipmentRows(batches).map((row) => ({ ...row, quantity: '' }))
                : buildShipmentRows(batches),
        );
        setSplitSeq(1);
        // This re-runs when the orders land a moment after opening; it must not blank the
        // name the peek has already filled in - so it re-seeds with it, not with ''.
        setReference(nextReference?.reference ?? '');
        setDispatchDate('');
        setTransportReference('');
        setDestinationId(null);
        setNotes('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batches, orderLines.length]);

    /**
     * What each product has on the load, for the line under its name: how many batches and
     * what they hold between them. Counted per BATCH, not per line - a split batch has two
     * lines and one shelf.
     */
    const productTotals = useMemo(() => {
        const totals = new Map<string, { batchCount: number; available: number }>();
        const seen = new Set<string>();
        for (const row of rows) {
            if (seen.has(row.batchId)) continue;
            seen.add(row.batchId);
            const total = totals.get(row.itemId) ?? { batchCount: 0, available: 0 };
            totals.set(row.itemId, {
                batchCount: total.batchCount + 1,
                available: total.available + row.available,
            });
        }
        return totals;
    }, [rows]);

    /**
     * WHAT IS ACTUALLY GOING, which in product mode is not every row.
     *
     * A blank line is a batch somebody decided not to open, not a mistake: it keeps its order
     * picker and its place, so putting it on the load is typing a number rather than a re-open.
     * Zero is "not on this lorry", so the checks, the totals and the payload all read past it.
     * In batch mode nothing is blank unless somebody blanked it, and a blank there is still the
     * mistake the check has always called it.
     */
    const activeRows = useMemo(
        () =>
            groupByProduct
                ? rows.filter((row) => (Number(row.quantity) || 0) > 0)
                : rows,
        [groupByProduct, rows],
    );

    const { rowProblems, overfilled } = useMemo(
        () => checkShipment(activeRows, orderLines, outstanding),
        [activeRows, orderLines, outstanding],
    );

    // Over every row, blanks included: a blank sooner batch is exactly what the nudge is about.
    const nudges = useMemo(() => fefoNudges(rows), [rows]);

    /**
     * REQUIRED FOR THE SITE, OPTIONAL FOR THE BRAND.
     *
     * It used to be optional for both - "not decided yet" was a legitimate answer. What made it
     * required here is that the destination IS the lane, and the lane's transit time is now the
     * only thing that tells the brand when to expect the load. A site that ships to nowhere
     * named gets no expected arrival at all, which is the state this change exists to end.
     *
     * The brand keeps the blank: it records consignments after the fact, sometimes from a
     * message that never said where the lorry went, and refusing that entry would lose the
     * shipment rather than the date.
     */
    const destinationRequired = !onBehalfOf;
    const canSubmit =
        activeRows.length > 0 &&
        rowProblems.size === 0 &&
        overfilled.length === 0 &&
        (!destinationRequired || !!destinationId) &&
        !isSubmitting;

    const setRow = (lineId: string, patch: Partial<ShipmentRow>) =>
        setRows((current) =>
            current.map((row) => (row.lineId === lineId ? { ...row, ...patch } : row)),
        );

    const addSplit = (lineId: string) => {
        setRows((current) => splitRow(current, lineId, splitSeq));
        setSplitSeq((seq) => seq + 1);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await api.dispatch({
                lines: toDispatchLines(activeRows, orderLines),
                reference: reference.trim() || undefined,
                dispatchDate: dispatchDate.trim() || undefined,
                transportReference: transportReference.trim() || undefined,
                // The request field is warehouseId; on this shipment it means the destination.
                warehouseId: destinationId ?? undefined,
                notes: notes.trim() || undefined,
            });

            showToastSuccess({
                title: `${summarise(activeRows)} sent`,
                subtitle:
                    'One shipment, in transit to you. It is off the site’s stock and against the orders each batch names.',
            });
            onShipped();
            onClose();
        } catch (err: any) {
            showToastError({
                title: 'Nothing was sent',
                subtitle:
                    err?.error?.message ?? err?.message ?? 'The shipment could not be recorded.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!opened) return null;

    // The quantity track widens for the Units | Cases toggle only when a line can use it.
    const casePackOnOffer = rows.some((row) => hasCasePack(unitsPerCaseOf(row.itemId)));
    const rowClass = casePackOnOffer ? `${styles.row} ${styles.rowCases}` : styles.row;

    return (
        <Modal
            opened
            onClose={onClose}
            size="90%"
            centered
            title={
                <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                    {activeRows.length > 0 ? `Send ${summarise(activeRows)}` : 'New shipment'}
                </Text>
            }
        >
            <Stack gap={16}>
                {/*
                  * THE CONSIGNMENT FIRST, THEN WHAT IS ON IT.
                  *
                  * This used to open on the lines and ask for the shipment's own details
                  * underneath, which inverted the order people actually work in: you know the
                  * lorry, where it is going and when it left before you start counting what
                  * went onto it. Tab order follows the DOM, so this is also the order the form
                  * is filled in - and the destination, which the site must now answer for the
                  * expected arrival to be known at all, is the first thing asked rather than
                  * something below a grid already scrolled past.
                  *
                  * The lines keep their own messages: the over-fill warnings render under the
                  * grid, beside the rows they are about.
                  *
                  * NO HELPER SENTENCES. What each field is for is in its label and placeholder;
                  * the one line that still appears is the no-lanes hint, and it sits UNDER its
                  * picker so showing it does not drop that box below its neighbours.
                  */}
                {/*
                  * DateInput with a string value and an explicit format, the same pair the
                  * declaration grid and the receive grid already use. A typed date still works —
                  * these are filled by people who tab through a form — but nobody has to know
                  * what YYYY-MM-DD means to answer "when did it leave".
                  */}
                <div className={styles.consignment}>
                    <TextInput
                        size="xs"
                        label="Shipment name"
                        placeholder="Numbered on save"
                        value={reference}
                        onChange={(event) => setReference(event.currentTarget.value)}
                    />
                    <Select
                        size="xs"
                        label="Destination warehouse"
                        // Internal only: a consignment from a manufacturer lands at one of the
                        // brand's own warehouses, never at a channel FC — those are fed from
                        // inside, by distribution.
                        description={noLanesHint}
                        inputWrapperOrder={BELOW}
                        placeholder={
                            destinationRequired ? 'Pick a warehouse' : 'Not decided yet'
                        }
                        data={destinationOptions}
                        value={destinationId}
                        onChange={setDestinationId}
                        searchable
                        // Only the brand may leave it blank, so only the brand may clear it.
                        clearable={!destinationRequired}
                        withAsterisk={destinationRequired}
                        error={destinationRequired && !destinationId && rows.length > 0}
                    />
                    <DateInput
                        size="xs"
                        label="Dispatch date"
                        placeholder="Today if left blank"
                        valueFormat="YYYY-MM-DD"
                        clearable
                        value={dispatchDate || null}
                        onChange={(value) => setDispatchDate(value ?? '')}
                    />
                    <TextInput
                        size="xs"
                        label="Transport reference"
                        placeholder="Lorry, LR or tracking number"
                        value={transportReference}
                        onChange={(event) => setTransportReference(event.currentTarget.value)}
                    />
                </div>

                <div className={styles.grid}>
                    {/* ONE HEADER, for the whole load. It used to repeat per product. */}
                    <div className={`${rowClass} ${styles.head}`}>
                        <div>Product</div>
                        <div>Batch</div>
                        <div>Quantity</div>
                        <div>Fills order</div>
                        <div />
                    </div>

                    {rows.map((row, index) => {
                        const problem = rowProblems.get(row.lineId);
                        const nudge = nudges.get(row.lineId);
                        // The product's first line names it; the rest leave the cell blank.
                        const firstOfProduct =
                            rows.findIndex((entry) => entry.itemId === row.itemId) === index;
                        const totals = productTotals.get(row.itemId);
                        const severalBatches = (totals?.batchCount ?? 0) > 1;
                        // A batch on more than one line says so once, on its first row, rather
                        // than repeating the code and looking like a duplicate.
                        const splitOfBatch =
                            rows.filter((entry) => entry.batchId === row.batchId).length > 1;
                        // Nothing to split TOWARDS unless the product has a second order open;
                        // a batch made for one order may only answer that order or none.
                        const canSplit =
                            orderOptionsForRow(row, orderChoicesBySku.get(row.itemId) ?? [])
                                .length > 2;
                        return (
                            <div key={row.lineId} className={rowClass}>
                                {firstOfProduct ? (
                                    <div className={styles.textCell}>
                                        <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                                            {row.itemName}
                                        </Text>
                                        {severalBatches && totals && (
                                            <Text fz={11} c={neutralColors.textMuted}>
                                                {totals.batchCount} batches ·{' '}
                                                {describeShelf(totals.available)}
                                            </Text>
                                        )}
                                    </div>
                                ) : (
                                    <Text
                                        fz={11}
                                        c={neutralColors.textMuted}
                                        className={styles.textCell}
                                    >
                                        ↳ same product
                                    </Text>
                                )}
                                <div className={styles.textCell}>
                                    <Group gap={8} wrap="nowrap">
                                        <Text
                                            fz={13}
                                            fw={row.noBatch ? 400 : 600}
                                            c={
                                                row.noBatch
                                                    ? neutralColors.textMuted
                                                    : neutralColors.textPrimary
                                            }
                                        >
                                            {batchLabel(row)}
                                            {splitOfBatch && (
                                                <Text span fz={11} c={neutralColors.textMuted}>
                                                    {' '}
                                                    · split
                                                </Text>
                                            )}
                                        </Text>
                                        {/*
                                          * Dispatch order put this batch first among its
                                          * product's; the tag just makes that legible. Only
                                          * where there is a second batch to be ahead of.
                                          */}
                                        {firstOfProduct && severalBatches && !row.noBatch && (
                                            <StatusPill label="Send first" tone="info" />
                                        )}
                                    </Group>
                                    {!row.noBatch && (
                                        <Text fz={11} c={neutralColors.textMuted}>
                                            {row.expiryDate
                                                ? `Expires ${formatDate(row.expiryDate)}`
                                                : 'No expiry date'}
                                        </Text>
                                    )}
                                </div>
                                <div>
                                    {/*
                                      * UNITS, OR CASES WHERE THE PRODUCT HAS A CASE SIZE - the
                                      * same box Record production uses, which speaks units to
                                      * us whichever way it was typed. What is on the shelf
                                      * stays under it always, error or not: the ceiling is
                                      * most worth reading at the moment it was crossed.
                                      */}
                                    <QuantityInput
                                        size="xs"
                                        w={96}
                                        hideControls
                                        min={0}
                                        placeholder="0"
                                        aria-label={`Units of ${batchLabel(row)} to send`}
                                        value={row.quantity === '' ? '' : Number(row.quantity)}
                                        onChange={(units) =>
                                            setRow(row.lineId, {
                                                quantity: units === '' ? '' : String(units),
                                            })
                                        }
                                        error={problem?.quantity}
                                        description={describeShelf(row.available)}
                                        inputWrapperOrder={BELOW}
                                        unitsPerCase={unitsPerCaseOf(row.itemId)}
                                    />
                                    {nudge && (
                                        <Text fz={11} c={semanticColors.warning.dark}>
                                            {nudge}
                                        </Text>
                                    )}
                                </div>
                                {/*
                                  * The row's OWN order and stock, not every order at the site.
                                  * A batch answers what it was made against or it answers
                                  * nothing; offering the rest invited a run made for one order
                                  * to be sent against another by picking the wrong line.
                                  */}
                                <Select
                                    size="xs"
                                    data={orderOptionsForRow(
                                        row,
                                        orderChoicesBySku.get(row.itemId) ?? [],
                                    )}
                                    value={row.orderChoice}
                                    onChange={(value) =>
                                        setRow(row.lineId, { orderChoice: value ?? '' })
                                    }
                                    error={problem?.order}
                                    searchable
                                    description={describeOrder(row, orderLines, outstanding)}
                                    inputWrapperOrder={BELOW}
                                />
                                <Group gap={2} wrap="nowrap">
                                    {/*
                                      * THE WAY PAST A FULL ORDER. Send what it can take on this
                                      * line and put the remainder on the next one, instead of
                                      * dropping the order to get the shipment out.
                                      */}
                                    {canSplit && (
                                        <Tooltip label="Split across another order" withArrow>
                                            <Button
                                                size="xs"
                                                variant={ButtonVariant.SUBTLE}
                                                aria-label={`Split ${batchLabel(row)} across another order`}
                                                onClick={() => addSplit(row.lineId)}
                                            >
                                                <PlusIcon size={14} />
                                            </Button>
                                        </Tooltip>
                                    )}
                                    <Button
                                        size="xs"
                                        variant={ButtonVariant.SUBTLE}
                                        aria-label={`Take ${batchLabel(row)} off this shipment`}
                                        // The last line stays: an empty modal has nothing to
                                        // send. Counted on every line, not the filled ones -
                                        // blank lines are lines.
                                        disabled={rows.length === 1}
                                        onClick={() =>
                                            setRows((current) =>
                                                current.filter(
                                                    (entry) => entry.lineId !== row.lineId,
                                                ),
                                            )
                                        }
                                    >
                                        <TrashIcon size={14} />
                                    </Button>
                                </Group>
                            </div>
                        );
                    })}
                </div>

                {overfilled.map((message) => (
                    <Text key={message} fz={13} c={semanticColors.critical.primary}>
                        {message}
                    </Text>
                ))}

                <Textarea
                    size="xs"
                    label="Notes"
                    autosize
                    minRows={2}
                    value={notes}
                    onChange={(event) => setNotes(event.currentTarget.value)}
                />

                <Group justify="flex-end">
                    <Group gap={8}>
                        <Button variant={ButtonVariant.SUBTLE} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            variant={ButtonVariant.ACTION}
                            disabled={!canSubmit}
                            loading={isSubmitting}
                            onClick={() => void handleSubmit()}
                        >
                            Create shipment
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </Modal>
    );
};

/**
 * What is still wanted against the order this row picked.
 *
 * Shown beside the choice rather than only on failure, so the number that decides whether this
 * can go is visible while somebody is deciding how much to send.
 */
function describeOrder(
    row: ShipmentRow,
    orderLines: Parameters<typeof checkShipment>[1],
    outstanding: Map<string, number>,
): string {
    if (!row.orderChoice) return 'Goes back as stock';

    const line = orderLines.find(
        (entry) =>
            entry.productId === row.itemId &&
            (row.orderChoice === `po:${entry.productionOrderId}` ||
                row.orderChoice === `wo:${entry.workOrderId}`),
    );

    const left = line ? outstanding.get(line.workOrderId) : undefined;
    return left === undefined ? 'Fills this order' : `${formatCount(left)} left to fill`;
}

export default ShipmentBuilderModal;
